import React, { useEffect, useState } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { NAV_LINKS } from '../mock';
import { useSiteNav } from '../hooks/use-site-nav';

const LOGO = '/assets/khm-logo.png';

const linkTone = (active, idle) => (active ? 'text-gold' : idle);

const useScrolled = (threshold) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
};

const useBodyLock = (locked) => {
  useEffect(() => {
    document.body.style.overflow = locked ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [locked]);
};

const DesktopNav = ({ go, isActive }) => (
  <nav className="hidden lg:flex items-center gap-8">
    {NAV_LINKS.map((link) => (
      <a
        key={link.label}
        href={link.href}
        onClick={(e) => go(e, link.href)}
        className={`text-[10px] tracking-[0.22em] transition-colors duration-300 hover:text-gold ${linkTone(
          isActive(link.href),
          'text-neutral-300'
        )}`}
      >
        {link.label}
      </a>
    ))}
  </nav>
);

const DesktopBookCta = ({ go }) => (
  <a
    href="/contact"
    onClick={(e) => go(e, '/contact')}
    className="group hidden lg:inline-flex items-center gap-2.5 bg-gold px-6 py-2.5 text-[10px] tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-light"
  >
    BOOK KHM
    <ArrowRight size={11} className="transition-transform duration-300 group-hover:translate-x-0.5" />
  </a>
);

const MobileLink = ({ link, index, open, active, go }) => (
  <a
    href={link.href}
    onClick={(e) => go(e, link.href)}
    className={`font-serif text-3xl tracking-[0.15em] transition-[color,opacity,transform] duration-500 hover:text-gold ${linkTone(
      active,
      'text-neutral-200'
    )} ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    style={{ transitionDelay: open ? `${120 + index * 60}ms` : '0ms' }}
  >
    {link.label}
  </a>
);

const MobileMenu = ({ open, onClose, go, isActive }) => (
  <div
    className={`fixed inset-0 z-50 bg-ink flex flex-col transition-[opacity,visibility] duration-500 lg:hidden ${
      open ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
    }`}
  >
    <div className="flex h-20 items-center justify-between px-6">
      <img src={LOGO} alt="KHM — Kanwal Malik" className="h-11 w-auto" />
      <button
        onClick={onClose}
        aria-label="Close menu"
        className="flex h-10 w-10 items-center justify-center text-white transition-colors duration-300 hover:text-gold"
      >
        <X size={22} strokeWidth={1.5} />
      </button>
    </div>
    <nav className="flex flex-1 flex-col items-center justify-center gap-7">
      {NAV_LINKS.map((link, i) => (
        <MobileLink key={link.label} link={link} index={i} open={open} active={isActive(link.href)} go={go} />
      ))}
    </nav>
    <div className="px-6 pb-12">
      <a
        href="/contact"
        onClick={(e) => go(e, '/contact')}
        className="flex w-full items-center justify-center gap-2.5 bg-gold py-4 text-[11px] tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-gold-light"
      >
        BOOK KHM
        <ArrowRight size={12} />
      </a>
    </div>
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const scrolled = useScrolled(40);
  const { navTo, pathname } = useSiteNav();
  useBodyLock(open);

  const isHome = pathname === '/';
  const solid = scrolled || !isHome;

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => navTo(href), open ? 90 : 0);
  };

  const isActive = (href) => {
    if (href === '/') return isHome;
    return !href.startsWith('/#') && pathname.startsWith(href);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          solid ? 'bg-ink/90 backdrop-blur-md border-b border-white/10' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-6 md:px-10">
          <a href="/" onClick={(e) => go(e, '/')} aria-label="KHM — Kanwal Malik, home" className="flex items-center">
            <img src={LOGO} alt="KHM — Kanwal Malik" className="h-12 w-auto md:h-14" />
          </a>
          <DesktopNav go={go} isActive={isActive} />
          <DesktopBookCta go={go} />
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className="lg:hidden flex h-10 w-10 items-center justify-center text-white transition-colors duration-300 hover:text-gold"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </header>
      <MobileMenu open={open} onClose={() => setOpen(false)} go={go} isActive={isActive} />
    </>
  );
};

export default Header;
